import type {
  WidgetConfig,
  WidgetState,
  MessageResponse,
  WidgetConfigResponse,
  WidgetMessageEvent,
  WidgetPresenceResponse,
} from '../types';
import { DEFAULT_WIDGET_CONFIG, isOfflineMode } from '../types';
import { MessagingApiClient, ApiError } from '../services/api';
import { SessionManager } from '../services/session';
import { Poller } from '../services/poller';
import { WidgetSignalRClient } from '../services/signalr';
import { generateStyles } from '../styles/widget.css';
import { h, formatTime } from '../utils/dom';
import { ICONS } from '../utils/icons';

/** Unique ID counter for label/input association */
let _idCounter = 0;
function nextId(prefix: string): string {
  return `envy-${prefix}-${++_idCounter}`;
}

export class EnvyChatWidget extends HTMLElement {
  private shadow: ShadowRoot;
  private config!: WidgetConfig;
  private api!: MessagingApiClient;
  private sessionManager: SessionManager;
  private poller: Poller;
  /** Polls GET /api/widget/presence for the widget's lifetime: drives the offline form / hide mode before a conversation exists, and the header status line always. */
  private presencePoller: Poller;
  /** Presence changed while the panel was open — re-render on the next open so we don't wipe a half-filled form. */
  private presenceRenderPending = false;
  private signalr: WidgetSignalRClient | null = null;
  private signalrConnected = false;

  /** Agent typing state */
  private agentTyping: { name: string; timeout: ReturnType<typeof setTimeout> } | null = null;
  /** Debounce timer for visitor typing indicator */
  private visitorTypingTimeout: ReturnType<typeof setTimeout> | null = null;
  private visitorIsTyping = false;

  private state: WidgetState = {
    isOpen: false,
    isLoading: false,
    hasSession: false,
    hasContactInfo: false,
    smsOptedIn: false,
    conversations: [],
    activeConversation: null,
    messages: [],
    unreadCount: 0,
    error: null,
    visitorInfo: {},
    remoteConfig: null,
    isOnline: null,
  };

  // DOM references
  private bubbleEl!: HTMLElement;
  private panelEl!: HTMLElement;
  private badgeEl!: HTMLElement;
  private messagesEl!: HTMLElement;
  private inputAreaEl!: HTMLElement;

  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: 'closed' });
    this.sessionManager = new SessionManager();
    this.poller = new Poller(() => this.pollMessages(), 5000);
    this.presencePoller = new Poller(() => this.pollPresence(), 60000);
  }

  static get observedAttributes(): string[] {
    return [
      'data-api-key',
      'data-api-url',
      'data-position',
      'data-primary-color',
      'data-text-color',
      'data-bg-color',
      'data-header-text-color',
      'data-border-radius',
      'data-font-family',
      'data-greeting',
      'data-placeholder',
      'data-brand-name',
      'data-brand-logo',
      'data-status-text',
      'data-offline-mode',
      'data-offline-intro',
      'data-force-offline',
    ];
  }

  connectedCallback(): void {
    this.config = this.parseAttributes();

    if (!this.config.apiKey || !this.config.apiUrl) {
      console.error('[envy-chat] Missing required attributes: data-api-key and data-api-url');
      return;
    }

    this.api = new MessagingApiClient(this.config.apiUrl, this.config.apiKey);
    this.initSignalR();
    this.render();
    this.initSession();
    // Remote config carries a presence snapshot; keep it fresh afterwards (first poll after one interval).
    this.fetchRemoteConfig().finally(() => this.presencePoller.startDelayed());

    // Reduce polling when tab is hidden; reconnect SignalR on visibility
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.poller.setInterval(30000);
      } else {
        this.poller.setInterval(5000);
        if (this.state.activeConversation) {
          this.poller.pollNow();
        }
        // Presence may have changed while we were in the background
        this.presencePoller.pollNow();
        // Reconnect SignalR if it dropped while hidden
        if (this.signalr && !this.signalr.isConnected && this.state.hasSession) {
          this.signalr.connect();
        }
      }
    });
  }

  disconnectedCallback(): void {
    this.poller.stop();
    this.presencePoller.stop();
    this.signalr?.disconnect();
  }

  private parseAttributes(): WidgetConfig {
    const defaults = DEFAULT_WIDGET_CONFIG;
    return {
      apiKey: this.getAttribute('data-api-key') || '',
      apiUrl: this.getAttribute('data-api-url') || '',
      position: (this.getAttribute('data-position') as WidgetConfig['position']) || defaults.position,
      theme: {
        primaryColor: this.getAttribute('data-primary-color') || defaults.theme.primaryColor,
        textColor: this.getAttribute('data-text-color') || defaults.theme.textColor,
        backgroundColor: this.getAttribute('data-bg-color') || defaults.theme.backgroundColor,
        headerTextColor: this.getAttribute('data-header-text-color') || defaults.theme.headerTextColor,
        borderRadius: parseInt(this.getAttribute('data-border-radius') || String(defaults.theme.borderRadius), 10),
        fontFamily: this.getAttribute('data-font-family') || defaults.theme.fontFamily,
      },
      greeting: this.getAttribute('data-greeting') || defaults.greeting,
      placeholder: this.getAttribute('data-placeholder') || defaults.placeholder,
      requireContactInfo: defaults.requireContactInfo,
      contactFields: [...defaults.contactFields],
      offlineMessage: defaults.offlineMessage,
      offlineMode: (() => {
        const attr = this.getAttribute('data-offline-mode');
        return isOfflineMode(attr) ? attr : defaults.offlineMode;
      })(),
      offlineIntroText: this.getAttribute('data-offline-intro') || defaults.offlineIntroText,
      offlineStatusText: defaults.offlineStatusText,
      brandName: this.getAttribute('data-brand-name') || defaults.brandName,
      brandLogoUrl: this.getAttribute('data-brand-logo') || undefined,
      statusText: this.getAttribute('data-status-text') || defaults.statusText,
      poweredByVisible: defaults.poweredByVisible,
    };
  }

  // === Remote Config ===

  private async fetchRemoteConfig(): Promise<void> {
    try {
      const remoteConfig = await this.api.getWidgetConfig();
      this.state.remoteConfig = remoteConfig;
      this.applyRemoteConfig(remoteConfig);
    } catch (err) {
      console.debug('[envy-chat] Could not fetch remote config, using defaults:', err);
    }
  }

  private applyRemoteConfig(remote: WidgetConfigResponse): void {
    // Remote config overrides defaults but NOT explicit data-attribute values
    if (!this.hasAttribute('data-greeting') && remote.greeting) {
      this.config.greeting = remote.greeting;
    }
    if (remote.offlineMessage) {
      this.config.offlineMessage = remote.offlineMessage;
    }
    if (!this.hasAttribute('data-offline-mode') && isOfflineMode(remote.offlineMode)) {
      this.config.offlineMode = remote.offlineMode;
    }

    // Apply remote theme for fields not set via data attributes
    if (remote.theme) {
      if (!this.hasAttribute('data-primary-color') && remote.theme.primaryColor) {
        this.config.theme.primaryColor = remote.theme.primaryColor;
      }
      if (!this.hasAttribute('data-position') && remote.theme.position) {
        // Backend serialises its WidgetPosition enum as "BottomRight" / "BottomLeft"
        this.config.position = remote.theme.position === 'BottomLeft' ? 'bottom-left' : 'bottom-right';
      }
      if (!this.hasAttribute('data-brand-name') && !this.config.brandName && remote.theme.headerText) {
        this.config.brandName = remote.theme.headerText;
      }
    }

    // Presence snapshot (kept fresh by pollPresence afterwards)
    if (typeof remote.isOnline === 'boolean') {
      this.state.isOnline = this.forceOffline ? false : remote.isOnline;
    }

    // Re-render with updated config
    this.render();
  }

  // === Agent Presence (offline fallback) ===

  /** Dev aid: `data-force-offline` makes the client behave as if nobody is online. The server still decides for real. */
  private get forceOffline(): boolean {
    const attr = this.getAttribute('data-force-offline');
    return attr !== null && attr !== 'false';
  }

  private async pollPresence(): Promise<void> {
    // Keeps running for the widget's whole lifetime — even with a conversation open
    // the header status line reflects whether anyone is there to reply.
    // Let errors propagate so the Poller backs off.
    const presence: WidgetPresenceResponse = await this.api.getPresence();
    this.applyPresence(presence);
  }

  private applyPresence(presence: WidgetPresenceResponse): void {
    if (!this.hasAttribute('data-offline-mode') && isOfflineMode(presence.offlineMode)) {
      this.config.offlineMode = presence.offlineMode;
    }

    const isOnline = this.forceOffline ? false : presence.isOnline;
    if (this.state.isOnline === isOnline) return;
    this.state.isOnline = isOnline;

    if (this.state.activeConversation || this.state.isOpen) {
      // Never rebuild under the visitor: with a thread in progress the view doesn't
      // change (only the header status does), and an open panel may hold a half-typed
      // form. Update the header line in place; without a thread, switch views the next
      // time the panel is opened.
      this.updatePresenceIndicators();
      if (!this.state.activeConversation) {
        this.presenceRenderPending = true;
      }
      return;
    }

    this.render();
  }

  private currentStatusText(): string {
    return this.state.isOnline === false ? this.config.offlineStatusText : this.config.statusText;
  }

  /**
   * A freshly *arrived* offline auto-reply is proof nobody is online — flip the
   * indicator immediately instead of waiting for the next presence poll. Only ever
   * called with newly received messages, never with restored history (old auto-replies
   * say nothing about the present).
   */
  private noteAutoReplyEvidence(newMessages: MessageResponse[]): void {
    if (this.state.isOnline === false) return;
    if (newMessages.some(m => m.senderType === 'System' && m.senderName === 'Auto-Reply')) {
      this.state.isOnline = false;
      this.updatePresenceIndicators();
    }
  }

  /**
   * `hide_widget` mode: render nothing while nobody is online and no conversation is
   * in progress. Also holds back the very first paint when the embed pins
   * `data-offline-mode="hide_widget"` and presence is still unknown (no bubble flash).
   * Never hides an open panel.
   */
  private shouldHideWidget(): boolean {
    if (this.config.offlineMode !== 'hide_widget') return false;
    if (this.state.activeConversation || this.state.isOpen) return false;
    if (this.state.isOnline === false) return true;
    return this.state.isOnline === null && this.hasAttribute('data-offline-mode');
  }

  // === SignalR Real-Time Connection ===

  private initSignalR(): void {
    this.signalr = new WidgetSignalRClient(
      this.config.apiUrl,
      () => this.api.getSessionToken(),
      {
        onNewMessage: (msg: WidgetMessageEvent) => this.handleSignalRMessage(msg),
        onStatusChanged: (convId: string, status: string) => this.handleSignalRStatusChange(convId, status),
        onTypingIndicator: (_convId: string, senderName: string, isTyping: boolean) => this.handleAgentTyping(senderName, isTyping),
        onConnectionChanged: (connected: boolean) => {
          this.signalrConnected = connected;
          this.updatePresenceIndicators();
          if (connected) {
            // SignalR is live — stop polling to avoid duplicate fetches
            this.poller.stop();
          } else if (this.state.isOpen && this.state.activeConversation) {
            // SignalR dropped — fall back to polling
            this.poller.start();
          }
        },
      }
    );
  }

  private handleSignalRMessage(msg: WidgetMessageEvent): void {
    if (!this.state.activeConversation) return;
    if (msg.conversationId !== this.state.activeConversation.id) return;

    // Avoid duplicates
    if (this.state.messages.some(m => m.id === msg.id)) return;

    const messageResponse: MessageResponse = {
      id: msg.id,
      conversationId: msg.conversationId,
      content: msg.content,
      contentType: msg.contentType === 'Html' ? 'Html' : 'Text',
      senderType: msg.senderType,
      senderName: msg.senderName,
      createdAt: msg.createdAt,
    };

    this.state.messages.push(messageResponse);
    this.renderMessages();
    this.noteAutoReplyEvidence([messageResponse]);

    // Clear typing indicator when we receive a real message — and an agent replying
    // is equally good evidence that someone IS online.
    if (msg.senderType === 'Agent') {
      this.clearAgentTyping();
      if (this.state.isOnline === false) {
        this.state.isOnline = true;
        this.updatePresenceIndicators();
      }
    }

    // Track unread if panel is closed
    if (!this.state.isOpen) {
      this.state.unreadCount++;
      this.updateBadge();
    }

    // Send delivery acknowledgment
    this.signalr?.acknowledgeDelivery(msg.conversationId, msg.id);

    // If panel is open, mark as read immediately
    if (this.state.isOpen) {
      this.signalr?.markAsRead(msg.conversationId, msg.id);
    }
  }

  private handleSignalRStatusChange(conversationId: string, newStatus: string): void {
    if (!this.state.activeConversation) return;
    if (this.state.activeConversation.id !== conversationId) return;

    this.state.activeConversation = {
      ...this.state.activeConversation,
      status: newStatus as 'New' | 'Open' | 'Resolved' | 'Closed',
    };

    // If conversation was closed/resolved, show a system message
    if (newStatus === 'Closed' || newStatus === 'Resolved') {
      const statusMsg: MessageResponse = {
        id: `status-${Date.now()}`,
        conversationId,
        content: newStatus === 'Closed'
          ? 'This conversation has been closed.'
          : 'This conversation has been resolved.',
        contentType: 'Text',
        senderType: 'System',
        createdAt: new Date().toISOString(),
      };
      this.state.messages.push(statusMsg);
      this.renderMessages();
    }
  }

  private handleAgentTyping(senderName: string, isTyping: boolean): void {
    if (isTyping) {
      // Clear any existing timeout
      if (this.agentTyping) {
        clearTimeout(this.agentTyping.timeout);
      }
      // Auto-clear after 5 seconds in case we miss the "stopped" event
      const timeout = setTimeout(() => this.clearAgentTyping(), 5000);
      this.agentTyping = { name: senderName, timeout };
      this.renderTypingIndicator();
    } else {
      this.clearAgentTyping();
    }
  }

  private clearAgentTyping(): void {
    if (this.agentTyping) {
      clearTimeout(this.agentTyping.timeout);
      this.agentTyping = null;
      this.renderTypingIndicator();
    }
  }

  private renderTypingIndicator(): void {
    if (!this.messagesEl) return;

    // Remove existing typing indicator
    const existing = this.messagesEl.querySelector('.typing-indicator-wrap');
    if (existing) existing.remove();

    if (this.agentTyping) {
      const wrap = h('div', { class: 'typing-indicator-wrap', style: 'display: flex; flex-direction: column; align-items: flex-start;' });
      const dots = h('div', { class: 'typing-indicator' });
      dots.innerHTML = '<span></span><span></span><span></span>';
      wrap.appendChild(dots);

      const label = h('div', {
        class: 'message-meta',
        style: 'margin-top: 2px;',
      }, `${this.agentTyping.name} is typing`);
      wrap.appendChild(label);

      this.messagesEl.appendChild(wrap);
      this.scrollToBottom();
    }
  }

  /** Send visitor typing status to dashboard agents via SignalR */
  private sendVisitorTyping(isTyping: boolean): void {
    if (!this.state.activeConversation || !this.signalr) return;
    this.signalr.sendTypingIndicator(this.state.activeConversation.id, isTyping);
  }

  // === Session Management ===

  private async initSession(): Promise<void> {
    const existingSession = this.sessionManager.getValidSession();
    if (existingSession) {
      this.api.setSessionToken(existingSession.token);
      this.state.hasSession = true;

      // Connect SignalR with existing token
      this.signalr?.connect();

      // Restore visitor info from localStorage
      const storedInfo = this.sessionManager.getVisitorInfo();
      if (storedInfo) {
        this.state.visitorInfo = storedInfo;
        // Only mark contact info complete if we have name + email at minimum
        this.state.hasContactInfo = !!(storedInfo.name && storedInfo.email);
      }

      // Restore active conversation from localStorage, falling back to backend
      const storedConversation = this.sessionManager.getActiveConversation();
      if (storedConversation) {
        this.state.activeConversation = storedConversation;
        // Join conversation group for real-time updates
        this.signalr?.joinConversation(storedConversation.id);
        // Fetch messages for the restored conversation (this also re-renders)
        this.loadConversationMessages(storedConversation.id);
      } else {
        // No local conversation — check backend for existing active conversations
        await this.restoreConversationFromBackend();
      }
      return;
    }

    try {
      const visitorId = this.sessionManager.getVisitorId() || undefined;
      const session = await this.api.createSession(visitorId);
      this.sessionManager.saveSession(session);
      this.api.setSessionToken(session.token);
      this.state.hasSession = true;

      // Connect SignalR with new token
      this.signalr?.connect();

      // Check if we have stored visitor info from a previous session
      const storedInfo = this.sessionManager.getVisitorInfo();
      if (storedInfo?.name && storedInfo?.email) {
        this.state.visitorInfo = storedInfo;
        this.state.hasContactInfo = true;
      }

      // Check backend for existing active conversations from a previous session
      await this.restoreConversationFromBackend();
    } catch (err) {
      console.warn('[envy-chat] Failed to create session:', err);
      this.state.error = 'Unable to connect. Please try again later.';
      this.updateErrorBanner();
    }
  }

  /** Try to restore the most recent active conversation from the backend */
  private async restoreConversationFromBackend(): Promise<void> {
    try {
      const conversations = await this.api.getConversations();
      if (conversations.length > 0) {
        // Resume the most recent active conversation
        const latest = conversations[0];
        this.state.activeConversation = latest;
        this.sessionManager.saveActiveConversation(latest);

        // Restore visitor info from the conversation if we don't have it locally
        if (!this.state.hasContactInfo && latest.visitor) {
          const info = {
            name: latest.visitor.name,
            email: latest.visitor.email,
            phone: latest.visitor.phone,
          };
          this.state.visitorInfo = info;
          this.state.hasContactInfo = !!(info.name && info.email);
          if (this.state.hasContactInfo) {
            this.sessionManager.saveVisitorInfo(info);
          }
        }

        // Join conversation group for real-time updates
        this.signalr?.joinConversation(latest.id);
        // Fetch messages and re-render
        this.loadConversationMessages(latest.id);
        return;
      }
    } catch (err) {
      console.debug('[envy-chat] Could not fetch existing conversations:', err);
    }

    // No existing conversation — just re-render (shows contact form or empty chat)
    if (this.state.hasContactInfo) {
      this.render();
    }
  }

  /** Load messages for a restored conversation and re-render */
  private async loadConversationMessages(conversationId: string): Promise<void> {
    try {
      const result = await this.api.getMessages(conversationId);
      this.state.messages = result.data;
      // Re-render to show the restored messages
      this.render();
      // Re-apply the open/closed state
      if (this.state.isOpen) {
        this.panelEl.classList.add('open');
        this.poller.start();
      }
    } catch (err) {
      console.warn('[envy-chat] Failed to restore conversation messages:', err);
      // Conversation may have been closed/deleted — clear it
      this.state.activeConversation = null;
      this.sessionManager.clearActiveConversation();
    }
  }

  // === Polling ===

  private async pollMessages(): Promise<void> {
    if (!this.state.activeConversation) return;

    // Let errors propagate to the Poller so it can apply backoff
    const result = await this.api.getMessages(this.state.activeConversation.id);
    const newMessages = result.data;

    if (newMessages.length > this.state.messages.length) {
      const addedCount = newMessages.length - this.state.messages.length;
      const added = newMessages.slice(this.state.messages.length);
      this.state.messages = newMessages;
      this.renderMessages();
      this.noteAutoReplyEvidence(added);

      // Update unread if panel is closed
      if (!this.state.isOpen) {
        this.state.unreadCount += addedCount;
        this.updateBadge();
      }
    }
  }

  // === Rendering ===

  private render(): void {
    this.shadow.innerHTML = '';
    this.presenceRenderPending = false;

    if (this.shouldHideWidget()) {
      return; // presencePoller keeps running and re-renders when someone comes online
    }

    // Style
    const style = document.createElement('style');
    style.textContent = generateStyles(this.config.theme, this.config.position);
    this.shadow.appendChild(style);

    // Chat bubble
    this.bubbleEl = this.createBubble();
    this.shadow.appendChild(this.bubbleEl);

    // Chat panel
    this.panelEl = this.createPanel();
    this.shadow.appendChild(this.panelEl);

    // A rebuilt messages area starts at scrollTop 0 — land on the newest message
    if (this.messagesEl && this.state.messages.length > 0) {
      this.scrollToBottom();
    }
  }

  private createBubble(): HTMLElement {
    const bubble = h('button', {
      class: 'chat-bubble',
      'aria-label': this.state.isOpen ? 'Close chat' : 'Open chat',
      'aria-expanded': this.state.isOpen ? 'true' : 'false',
      'aria-haspopup': 'dialog',
      onClick: () => this.toggle(),
    });

    bubble.innerHTML = this.state.isOpen ? ICONS.close : ICONS.chat;

    // Add close-icon class for slightly smaller icon
    if (this.state.isOpen) {
      const svg = bubble.querySelector('svg');
      if (svg) svg.classList.add('close-icon');
    }

    this.badgeEl = h('span', {
      class: `badge ${this.state.unreadCount === 0 ? 'hidden' : ''}`,
      'aria-live': 'polite',
      'aria-atomic': 'true',
    });
    this.badgeEl.textContent = this.state.unreadCount > 0
      ? `${this.state.unreadCount} unread`
      : '';
    bubble.appendChild(this.badgeEl);

    // Visible badge text (number only), SR text handled by aria
    if (this.state.unreadCount > 0) {
      this.badgeEl.textContent = String(this.state.unreadCount);
      this.badgeEl.setAttribute('aria-label', `${this.state.unreadCount} unread messages`);
    }

    return bubble;
  }

  private createPanel(): HTMLElement {
    const panel = h('div', {
      class: `chat-panel ${this.state.isOpen ? 'open' : ''}`,
      role: 'dialog',
      'aria-label': 'Chat with support',
    });

    // Escape key closes the panel
    panel.addEventListener('keydown', (e: Event) => {
      const ke = e as KeyboardEvent;
      if (ke.key === 'Escape' && this.state.isOpen) {
        this.toggle();
        this.bubbleEl.focus();
      }
    });

    // Header
    panel.appendChild(this.createHeader());

    // Error banner with live region for screen reader announcements
    const errorBanner = h('div', {
      class: 'error-banner',
      role: 'alert',
      'aria-live': 'assertive',
      style: this.state.error ? '' : 'display:none',
    }, this.state.error || '');
    errorBanner.setAttribute('data-role', 'error');
    panel.appendChild(errorBanner);

    // Body — offline lead-capture form, pre-chat contact form, or messages
    if (this.state.isOnline === false && !this.state.activeConversation) {
      panel.appendChild(this.createOfflineForm());
    } else if (this.config.requireContactInfo && !this.state.hasContactInfo) {
      panel.appendChild(this.createContactForm());
    } else {
      this.messagesEl = this.createMessagesArea();
      panel.appendChild(this.messagesEl);

      this.inputAreaEl = this.createInputArea();
      panel.appendChild(this.inputAreaEl);
    }

    // Panel footer (version info + powered by)
    panel.appendChild(this.createPanelFooter());

    return panel;
  }

  private createHeader(): HTMLElement {
    const header = h('div', { class: 'chat-header', role: 'banner' });

    if (this.config.brandLogoUrl) {
      header.appendChild(h('img', {
        class: 'brand-logo',
        src: this.config.brandLogoUrl,
        alt: '', // Decorative — brand name is in text next to it
      }));
    }

    const textContainer = h('div', { class: 'header-text' });
    if (this.config.brandName) {
      textContainer.appendChild(h('h2', { class: 'brand-name' }, this.config.brandName));
    }
    textContainer.appendChild(h('div', { class: 'status-text' }, this.currentStatusText()));
    header.appendChild(textContainer);

    // Close control inside the panel — on mobile the fullscreen panel covers the
    // bubble, so this is the only way back to the page (and standard UX on desktop).
    const closeBtn = h('button', {
      class: 'header-close',
      type: 'button',
      'aria-label': 'Close chat',
      onClick: () => {
        if (this.state.isOpen) this.toggle();
      },
    });
    closeBtn.innerHTML = ICONS.close;
    header.appendChild(closeBtn);

    return header;
  }

  /** Footer dot: agent presence (green = someone is online to reply, grey = away). Not socket status. */
  private presenceDotClass(): string {
    return `presence-dot ${this.state.isOnline === true ? 'online' : 'away'}`;
  }

  /** ⓘ tooltip: version, agent presence, and the widget's own connection state. */
  private infoTooltipText(): string {
    const presence = this.state.isOnline === true ? 'Team online' : this.state.isOnline === false ? 'Team away' : 'Checking availability';
    const connection = this.signalrConnected ? 'Live updates on' : 'Live updates reconnecting';
    return `v${__WIDGET_VERSION__} · ${presence} · ${connection}`;
  }

  /** Refresh header status line, footer dot and tooltip in place (no re-render). */
  private updatePresenceIndicators(): void {
    const status = this.shadow.querySelector('.status-text');
    if (status) status.textContent = this.currentStatusText();
    const dot = this.shadow.querySelector('.presence-dot');
    if (dot) {
      dot.className = this.presenceDotClass();
      dot.setAttribute('aria-label', this.state.isOnline === true ? 'Team online' : 'Team away');
    }
    const tooltip = this.shadow.querySelector('.version-tooltip');
    if (tooltip) tooltip.textContent = this.infoTooltipText();
  }

  private createPanelFooter(): HTMLElement {
    const footer = h('div', { class: 'panel-footer' });

    // Version info + connection status (bottom-left)
    const versionInfo = h('div', { class: 'version-info' });
    versionInfo.innerHTML = ICONS.info;
    const presenceDot = h('span', {
      class: this.presenceDotClass(),
      role: 'img',
      'aria-label': this.state.isOnline === true ? 'Team online' : 'Team away',
    });
    versionInfo.appendChild(presenceDot);
    const tooltip = h('span', { class: 'version-tooltip' }, this.infoTooltipText());
    versionInfo.appendChild(tooltip);
    footer.appendChild(versionInfo);

    // SMS opt-out toggle (shown only when visitor has opted in)
    if (this.state.smsOptedIn && this.state.visitorInfo.phone) {
      const smsToggle = h('div', {
        style: 'display: flex; align-items: center; gap: 4px; font-size: 11px; color: #6b7280; cursor: pointer; padding: 0 4px;',
      });
      smsToggle.innerHTML = '&#x1F4F1;'; // phone emoji
      const label = h('span', {}, 'SMS ON');
      smsToggle.appendChild(label);
      smsToggle.title = 'Click to opt out of SMS notifications';
      smsToggle.addEventListener('click', async () => {
        const phone = this.state.visitorInfo.phone;
        if (!phone) return;
        try {
          await this.api.smsOptOut(phone);
          this.state.smsOptedIn = false;
          label.textContent = 'SMS OFF';
          smsToggle.title = 'SMS notifications disabled';
          smsToggle.style.opacity = '0.5';
        } catch (err) {
          console.debug('[envy-chat] SMS opt-out failed:', err);
        }
      });
      footer.appendChild(smsToggle);
    }

    // Powered by (centered)
    if (this.config.poweredByVisible) {
      footer.appendChild(h('div', { class: 'powered-by' },
        h('a', { href: 'https://envycentral.com', target: '_blank', rel: 'noopener' },
          'Powered by EnvyCentral'
        )
      ));
    }

    return footer;
  }

  private createContactForm(): HTMLElement {
    const form = h('form', {
      class: 'contact-form',
      onSubmit: (e: Event) => {
        e.preventDefault();
        this.handleContactSubmit(e.target as HTMLFormElement);
      },
    });

    form.appendChild(h('div', { style: 'font-size: 14px; color: #6b7280; margin-bottom: 4px;' },
      'Before we start, please share your info so we can follow up with you.'
    ));

    this.appendContactFields(form, { emailRequired: true });

    form.appendChild(h('button', { type: 'submit', class: 'submit-btn' }, 'Start Chat'));

    return form;
  }

  /**
   * Offline lead-capture form (nobody is online): contact details + the message
   * itself, submitted in one go. The server stores it as a conversation, Envy.Leads
   * turns it into a Lead, and the visitor immediately sees the offline auto-reply.
   */
  private createOfflineForm(): HTMLElement {
    const form = h('form', {
      class: 'contact-form offline-form',
      'aria-label': 'Leave us a message',
      onSubmit: (e: Event) => {
        e.preventDefault();
        this.handleOfflineSubmit(e.target as HTMLFormElement);
      },
    });

    form.appendChild(h('div', { class: 'greeting-message offline-intro' }, this.config.offlineIntroText));

    // Email OR phone is required (server rule) — the HTML `required` attribute can't express "either", so we validate on submit.
    this.appendContactFields(form, { emailRequired: false });
    form.appendChild(h('div', { class: 'form-hint' }, 'We need an email or phone number to get back to you.'));

    {
      const id = nextId('message');
      const label = h('label', { for: id }, 'Message *');
      const textarea = h('textarea', {
        id,
        name: 'message',
        placeholder: 'How can we help?',
        required: 'true',
        rows: '4',
      });
      form.appendChild(label);
      form.appendChild(textarea);
    }

    form.appendChild(h('button', { type: 'submit', class: 'submit-btn' }, 'Send'));

    return form;
  }

  /** Name / Email / Phone (+ SMS opt-in) inputs and the honeypot, shared by both forms. */
  private appendContactFields(form: HTMLElement, opts: { emailRequired: boolean }): void {
    // Name is always required — shown regardless of contactFields config
    {
      const id = nextId('name');
      const label = h('label', { for: id }, 'Name *');
      const input = h('input', { type: 'text', id, name: 'name', placeholder: 'Your name', required: 'true', autocomplete: 'name' });
      form.appendChild(label);
      form.appendChild(input);
    }

    {
      const id = nextId('email');
      const label = h('label', { for: id }, opts.emailRequired ? 'Email *' : 'Email');
      const input = h('input', { type: 'email', id, name: 'email', placeholder: 'you@example.com', required: opts.emailRequired, autocomplete: 'email' });
      form.appendChild(label);
      form.appendChild(input);
    }

    // Phone is always shown (optional) — needed for SMS notifications
    {
      const id = nextId('phone');
      const label = h('label', { for: id }, opts.emailRequired ? 'Phone (optional)' : 'Phone');
      const input = h('input', {
        type: 'tel',
        id,
        name: 'phone',
        placeholder: '(555) 123-4567',
        autocomplete: 'tel',
      });
      form.appendChild(label);
      form.appendChild(input);

      // SMS opt-in checkbox (shown dynamically when phone has a value)
      const optInContainer = h('div', {
        class: 'sms-opt-in',
        style: 'display: none; margin: 4px 0 8px 0;',
      });
      const checkId = nextId('sms-optin');
      const checkbox = h('input', {
        type: 'checkbox',
        id: checkId,
        name: 'smsOptIn',
        style: 'width: auto; margin-right: 6px; vertical-align: middle;',
      }) as HTMLInputElement;
      const optInLabel = h('label', {
        for: checkId,
        style: 'font-size: 12px; color: #6b7280; line-height: 1.3; display: inline; cursor: pointer;',
      }, 'I agree to receive SMS notifications about this conversation. Message & data rates may apply. Reply STOP to opt out.');
      optInContainer.appendChild(checkbox);
      optInContainer.appendChild(optInLabel);
      form.appendChild(optInContainer);

      // Show/hide opt-in checkbox based on phone input
      const phoneInput = input as HTMLInputElement;
      phoneInput.addEventListener('input', () => {
        const hasPhone = phoneInput.value.trim().length > 0;
        optInContainer.style.display = hasPhone ? 'block' : 'none';
        if (!hasPhone) {
          checkbox.checked = false;
        }
      });
    }

    // Honeypot field for spam prevention — hidden from assistive tech
    const honeypot = h('input', {
      type: 'text',
      name: 'website',
      class: 'hp-field',
      tabindex: '-1',
      autocomplete: 'off',
      'aria-hidden': 'true',
    });
    form.appendChild(honeypot);
  }

  /** Read the shared contact fields off a submitted form. */
  private readContactForm(form: HTMLFormElement): {
    honeypotTripped: boolean;
    name?: string;
    email?: string;
    phone?: string;
    smsOptIn: boolean;
  } {
    const honeypot = form.querySelector('input[name="website"]') as HTMLInputElement | null;
    const formData = new FormData(form);
    const phone = (formData.get('phone') as string)?.trim() || undefined;
    const smsOptIn = (form.querySelector('input[name="smsOptIn"]') as HTMLInputElement | null)?.checked ?? false;
    return {
      honeypotTripped: !!honeypot?.value,
      name: (formData.get('name') as string)?.trim() || undefined,
      email: (formData.get('email') as string)?.trim() || undefined,
      phone,
      smsOptIn: smsOptIn && !!phone,
    };
  }

  /** Persist visitor info locally and record TCPA consent with the backend when opted in. */
  private async saveVisitorInfoAndConsent(info: { name?: string; email?: string; phone?: string }, smsOptIn: boolean): Promise<void> {
    this.state.visitorInfo = info;
    this.state.smsOptedIn = smsOptIn && !!info.phone;

    // Persist to localStorage so it survives page navigation
    this.sessionManager.saveVisitorInfo(this.state.visitorInfo);

    // Record SMS consent with the backend if opted in
    if (this.state.smsOptedIn && info.phone) {
      try {
        await this.api.recordSmsConsent({
          phone: info.phone,
          consentText: 'I agree to receive SMS notifications about this conversation. Message & data rates may apply. Reply STOP to opt out.',
          visitorName: info.name,
          visitorEmail: info.email,
        });
      } catch (err) {
        console.debug('[envy-chat] Failed to record SMS consent:', err);
        // Don't block the chat from starting
      }
    }
  }

  private createMessagesArea(): HTMLElement {
    const area = h('div', {
      class: 'messages-area',
      role: 'log',
      'aria-live': 'polite',
      'aria-label': 'Message history',
    });

    // Greeting message
    if (this.state.messages.length === 0) {
      area.appendChild(h('div', { class: 'greeting-message' }, this.config.greeting));
    }

    // Render existing messages
    for (const msg of this.state.messages) {
      area.appendChild(this.createMessageBubble(msg));
    }

    return area;
  }

  private createMessageBubble(msg: MessageResponse): HTMLElement {
    const wrapper = h('div', { style: `display: flex; flex-direction: column; ${msg.senderType === 'Visitor' ? 'align-items: flex-end;' : 'align-items: flex-start;'}` });

    const bubble = h('div', {
      class: `message-bubble ${msg.senderType.toLowerCase()}`,
    });
    bubble.textContent = msg.content;

    const meta = h('div', {
      class: `message-meta ${msg.senderType === 'Visitor' ? 'visitor-meta' : ''}`,
    });

    const senderLabel = msg.senderType === 'Visitor' ? 'You' : (msg.senderName || 'Support');
    meta.textContent = `${senderLabel} · ${formatTime(msg.createdAt)}`;

    wrapper.appendChild(bubble);
    wrapper.appendChild(meta);
    return wrapper;
  }

  private createInputArea(): HTMLElement {
    const area = h('div', { class: 'input-area' });

    const textarea = document.createElement('textarea');
    textarea.placeholder = this.config.placeholder;
    textarea.rows = 1;
    textarea.setAttribute('aria-label', 'Type a message');

    // Auto-resize + typing indicator
    textarea.addEventListener('input', () => {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 100) + 'px';

      // Debounced typing indicator dispatch
      if (textarea.value.trim() && !this.visitorIsTyping) {
        this.visitorIsTyping = true;
        this.sendVisitorTyping(true);
      }

      if (this.visitorTypingTimeout) clearTimeout(this.visitorTypingTimeout);
      this.visitorTypingTimeout = setTimeout(() => {
        if (this.visitorIsTyping) {
          this.visitorIsTyping = false;
          this.sendVisitorTyping(false);
        }
      }, 2000);
    });

    // Send on Enter (Shift+Enter for newline)
    textarea.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.handleSendMessage(textarea);
      }
    });

    const sendBtn = h('button', {
      class: 'send-btn',
      'aria-label': 'Send message',
      onClick: () => this.handleSendMessage(textarea),
    });
    sendBtn.innerHTML = ICONS.send;

    area.appendChild(textarea);
    area.appendChild(sendBtn);

    return area;
  }

  // === UI Updates (without full re-render) ===

  private renderMessages(): void {
    if (!this.messagesEl) return;

    this.messagesEl.innerHTML = '';

    if (this.state.messages.length === 0) {
      this.messagesEl.appendChild(h('div', { class: 'greeting-message' }, this.config.greeting));
    }

    for (const msg of this.state.messages) {
      this.messagesEl.appendChild(this.createMessageBubble(msg));
    }

    this.scrollToBottom();
  }

  private scrollToBottom(): void {
    if (this.messagesEl) {
      requestAnimationFrame(() => {
        this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
      });
    }
  }

  private updateBadge(): void {
    if (this.badgeEl) {
      const count = this.state.unreadCount;
      this.badgeEl.textContent = count > 0 ? String(count) : '';
      this.badgeEl.classList.toggle('hidden', count === 0);
      this.badgeEl.setAttribute('aria-label', count > 0 ? `${count} unread messages` : '');
    }
  }

  private updateErrorBanner(): void {
    const banner = this.panelEl?.querySelector('[data-role="error"]') as HTMLElement;
    if (banner) {
      banner.textContent = this.state.error || '';
      banner.style.display = this.state.error ? '' : 'none';
    }
  }

  // === Event Handlers ===

  private toggle(): void {
    this.state.isOpen = !this.state.isOpen;

    if (this.state.isOpen && this.presenceRenderPending && !this.state.activeConversation) {
      // Presence flipped while the panel was open last time — rebuild so the right
      // view (offline form vs. live chat) shows. render() honours isOpen.
      this.state.unreadCount = 0;
      this.render();
      requestAnimationFrame(() => {
        this.scrollToBottom();
        const field = this.shadow.querySelector('textarea, input[name="name"]') as HTMLElement | null;
        field?.focus();
      });
      return;
    }

    if (this.state.isOpen) {
      this.state.unreadCount = 0;
      this.updateBadge();

      if (this.state.activeConversation) {
        // Only start polling if SignalR is not connected
        if (!this.signalrConnected) {
          this.poller.start();
        }

        // Mark latest message as read
        const lastMsg = this.state.messages[this.state.messages.length - 1];
        if (lastMsg) {
          this.signalr?.markAsRead(this.state.activeConversation.id, lastMsg.id);
        }
      }
    } else {
      this.poller.stop();
    }

    // Update bubble icon
    this.bubbleEl.innerHTML = this.state.isOpen ? ICONS.close : ICONS.chat;
    if (this.state.isOpen) {
      const svg = this.bubbleEl.querySelector('svg');
      if (svg) svg.classList.add('close-icon');
    }
    this.bubbleEl.appendChild(this.badgeEl);
    this.bubbleEl.setAttribute('aria-label', this.state.isOpen ? 'Close chat' : 'Open chat');
    this.bubbleEl.setAttribute('aria-expanded', String(this.state.isOpen));

    // Toggle panel
    this.panelEl.classList.toggle('open', this.state.isOpen);

    // Focus textarea and show the latest message if opening
    if (this.state.isOpen) {
      requestAnimationFrame(() => {
        this.scrollToBottom();
        const textarea = this.shadow.querySelector('textarea') as HTMLTextAreaElement;
        textarea?.focus();
      });
    }
  }

  private async handleContactSubmit(form: HTMLFormElement): Promise<void> {
    const { honeypotTripped, name, email, phone, smsOptIn } = this.readContactForm(form);

    if (honeypotTripped) {
      // Bot detected — silently pretend success
      this.state.hasContactInfo = true;
      this.render();
      return;
    }

    // Require name and email as minimum
    if (!name || !email) {
      this.state.error = 'Please provide both your name and email address.';
      this.updateErrorBanner();
      return;
    }

    await this.saveVisitorInfoAndConsent({ name, email, phone }, smsOptIn);

    this.state.hasContactInfo = true;
    this.state.error = null;

    // Re-render to show messages view
    this.render();
    // Re-open the panel since render resets it
    this.state.isOpen = true;
    this.panelEl.classList.add('open');
    this.bubbleEl.innerHTML = ICONS.close;
    const svg = this.bubbleEl.querySelector('svg');
    if (svg) svg.classList.add('close-icon');
    this.bubbleEl.appendChild(this.badgeEl);

    requestAnimationFrame(() => {
      const textarea = this.shadow.querySelector('textarea') as HTMLTextAreaElement;
      textarea?.focus();
    });
  }

  /**
   * Offline form submit: contact details + message in one request. The server
   * decides for real whether anyone is online; if nobody is, the response carries
   * the persisted auto-reply and the thread stays open for follow-ups.
   */
  private async handleOfflineSubmit(form: HTMLFormElement): Promise<void> {
    const { honeypotTripped, name, email, phone, smsOptIn } = this.readContactForm(form);
    const message = ((new FormData(form).get('message') as string) || '').trim();

    if (honeypotTripped) {
      // Bot detected — silently swallow
      form.reset();
      return;
    }

    if (!name || !message) {
      this.state.error = 'Please tell us your name and what you need help with.';
      this.updateErrorBanner();
      return;
    }
    if (!email && !phone) {
      this.state.error = 'Please provide an email address or phone number so we can get back to you.';
      this.updateErrorBanner();
      return;
    }

    // Ensure session exists
    if (!this.state.hasSession) {
      await this.initSession();
      if (!this.state.hasSession) return;
    }

    const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement | null;
    if (submitBtn) submitBtn.disabled = true;

    await this.saveVisitorInfoAndConsent({ name, email, phone }, smsOptIn);
    this.state.hasContactInfo = !!(name && email);

    try {
      const conversation = await this.api.startConversation({
        subject: message.slice(0, 100),
        message,
        visitorName: name,
        visitorEmail: email,
        visitorPhone: phone,
      });

      this.state.activeConversation = conversation;
      this.sessionManager.saveActiveConversation(conversation);
      this.signalr?.joinConversation(conversation.id);

      // Show the submission and the auto-reply right away (server-persisted; re-synced below)
      this.state.messages = [
        {
          id: 'temp-' + Date.now(),
          conversationId: conversation.id,
          content: message,
          contentType: 'Text',
          senderType: 'Visitor',
          senderName: name,
          createdAt: new Date().toISOString(),
        },
      ];
      if (conversation.autoReply) {
        this.state.messages.push(conversation.autoReply);
      } else if (conversation.capturedOffline) {
        // Older backend without autoReply in the response — fall back to local copy until the re-sync
        this.state.messages.push({
          id: 'temp-auto-reply',
          conversationId: conversation.id,
          content: this.config.offlineMessage,
          contentType: 'Text',
          senderType: 'System',
          senderName: 'Auto-Reply',
          createdAt: new Date().toISOString(),
        });
      }

      this.state.error = null;
      this.render(); // switches to the thread view; honours isOpen

      if (!this.signalrConnected) {
        this.poller.start();
      }

      requestAnimationFrame(() => {
        const textarea = this.shadow.querySelector('textarea') as HTMLTextAreaElement | null;
        textarea?.focus();
      });

      // Sync with the server copy (ids, timestamps)
      const result = await this.api.getMessages(conversation.id);
      this.state.messages = result.data;
      this.renderMessages();
      if (conversation.capturedOffline === false && this.state.isOnline === false) {
        // Someone came online between the form render and the submit
        this.state.isOnline = true;
        this.updatePresenceIndicators();
      }
    } catch (err) {
      console.error('[envy-chat] Offline submit error:', err);
      if (err instanceof ApiError && err.status === 400) {
        let serverMessage: string | undefined;
        try {
          serverMessage = (JSON.parse(err.body) as { message?: string }).message;
        } catch {
          /* non-JSON body */
        }
        this.state.error = serverMessage || 'Please check your details and try again.';
      } else if (err instanceof ApiError && err.status === 429) {
        this.state.error = 'Too many messages. Please wait a moment.';
      } else {
        this.state.error = 'Failed to send your message. Please try again.';
      }
      this.updateErrorBanner();
      if (submitBtn) submitBtn.disabled = false;
    }
  }

  private async handleSendMessage(textarea: HTMLTextAreaElement): Promise<void> {
    const content = textarea.value.trim();
    if (!content) return;

    // Ensure session exists
    if (!this.state.hasSession) {
      await this.initSession();
      if (!this.state.hasSession) return;
    }

    textarea.value = '';
    textarea.style.height = 'auto';

    // Stop typing indicator on send
    if (this.visitorIsTyping) {
      this.visitorIsTyping = false;
      if (this.visitorTypingTimeout) clearTimeout(this.visitorTypingTimeout);
      this.sendVisitorTyping(false);
    }

    try {
      if (!this.state.activeConversation) {
        // Ensure we have visitor info before starting a conversation
        if (!this.state.visitorInfo.name || !this.state.visitorInfo.email) {
          this.state.error = 'Please provide your name and email to start a conversation.';
          this.updateErrorBanner();
          textarea.value = content;
          return;
        }

        // Start new conversation
        const conversation = await this.api.startConversation({
          subject: content.slice(0, 100),
          message: content,
          visitorName: this.state.visitorInfo.name,
          visitorEmail: this.state.visitorInfo.email,
          visitorPhone: this.state.visitorInfo.phone,
        });

        this.state.activeConversation = conversation;

        // Persist the active conversation so it survives page navigation
        this.sessionManager.saveActiveConversation(conversation);

        // Join SignalR conversation group for real-time updates
        this.signalr?.joinConversation(conversation.id);

        // Optimistic message render
        this.state.messages.push({
          id: 'temp-' + Date.now(),
          conversationId: conversation.id,
          content,
          contentType: 'Text',
          senderType: 'Visitor',
          senderName: this.state.visitorInfo.name,
          createdAt: new Date().toISOString(),
        });
        this.renderMessages();

        // Start polling as fallback (stopped automatically when SignalR connects)
        if (!this.signalrConnected) {
          this.poller.start();
        }

        // Fetch actual messages to sync
        const result = await this.api.getMessages(conversation.id);
        this.state.messages = result.data;
        this.renderMessages();
        if (conversation.capturedOffline && this.state.isOnline !== false) {
          this.state.isOnline = false;
          this.updatePresenceIndicators();
        }
      } else {
        // Optimistic render
        this.state.messages.push({
          id: 'temp-' + Date.now(),
          conversationId: this.state.activeConversation.id,
          content,
          contentType: 'Text',
          senderType: 'Visitor',
          senderName: this.state.visitorInfo.name,
          createdAt: new Date().toISOString(),
        });
        this.renderMessages();

        // Send message
        const msg = await this.api.sendMessage(this.state.activeConversation.id, content);

        // Replace optimistic message with real one
        const tempIdx = this.state.messages.findIndex(m => m.id.startsWith('temp-'));
        if (tempIdx !== -1) {
          this.state.messages[tempIdx] = msg;
        }
        this.renderMessages();

        // Re-sync with the server copy: while nobody is online the server may have
        // appended an offline auto-reply before responding, and it must show even if
        // the SignalR push is missed (connection mid-rejoin, transport fallback, …).
        const result = await this.api.getMessages(this.state.activeConversation.id);
        if (result.data.length > this.state.messages.length) {
          const known = new Set(this.state.messages.map(m => m.id));
          const added = result.data.filter(m => !known.has(m.id));
          this.state.messages = result.data;
          this.renderMessages();
          this.noteAutoReplyEvidence(added);
        }
      }

      this.state.error = null;
      this.updateErrorBanner();

      // The indicator can be up to a poll interval stale — re-check right after sending,
      // when the visitor is most likely to be looking at it.
      this.presencePoller.pollNow();
    } catch (err) {
      console.error('[envy-chat] Send error:', err);
      if (err instanceof ApiError && err.status === 429) {
        this.state.error = 'Too many messages. Please wait a moment.';
      } else {
        this.state.error = 'Failed to send message. Please try again.';
      }
      this.updateErrorBanner();

      // Restore the message to the textarea
      textarea.value = content;
    }
  }
}
